import React from 'react'
import { useHistory } from 'react-router-dom'
import { observer } from 'mobx-react-lite'

import { Divider } from '@mui/material'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import makeStyles from '@mui/styles/makeStyles'
import createStyles from '@mui/styles/createStyles'

import ClickAwayListener from '@mui/material/ClickAwayListener'
import Grow from '@mui/material/Grow'
import MenuItem from '@mui/material/MenuItem'
import MenuList from '@mui/material/MenuList'
import Paper from '@mui/material/Paper'
import Popper from '@mui/material/Popper'
import { useStores } from '@trivie/core'
import { Avatar } from '@trivie/ui-web/components/avatar'
import { Text } from '@trivie/ui-web/components/text'
import { color } from '@trivie/ui-web/theme/color'

const useStyles = makeStyles(() =>
  createStyles({
    avatarButton: {
      '&:focus': {
        boxSizing: 'content-box',
        border: '1px solid rgba(0,0,0,0.2)',
      },
    },
    navItem: {
      '&:hover': {
        backgroundColor: color.palette.paperBorder,
      },
      height: 50,
      width: '100%',
      cursor: 'pointer',
    },
  }),
)

export const ProfileDropdown = observer(() => {
  const { userStore, uiStore } = useStores()
  const { user, logout } = userStore

  const [open, setOpen] = React.useState(false)
  const logOutElement = React.useRef<HTMLDivElement>(null)
  const anchorRef = React.useRef<HTMLDivElement>(null)

  const history = useHistory()
  const classes = useStyles()

  const handleMenuItemClick = (index: number) => {
    if (index === 3) {
      logout()
      history.replace('/login')
    }
    setOpen(false)
  }

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen)
    //Should check focus-trap alternative
    setTimeout(() => {
      logOutElement?.current?.focus()
    }, 250)
  }

  const handleClose = (event: any) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return
    }

    setOpen(false)
  }

  return (
    <div
      style={{
        width: '100%',
        display: 'flex',
        justifyContent: 'flex-end',
        maxWidth: '1470px',
        paddingRight: 20,
      }}
    >
      <div style={CONTAINER}>
        <ButtonGroup
          style={BUTTON_CONTAINER}
          variant="contained"
          color="primary"
          ref={anchorRef}
          aria-label={`${user?.first_name} ${user?.last_name}`}
        >
          <Button
            className={classes.avatarButton}
            role="button"
            id="contentSearch"
            style={AVATAR_BUTTON}
            size="small"
            aria-controls={open ? 'split-button-menu' : undefined}
            aria-expanded={open ? 'true' : undefined}
            aria-haspopup="menu"
            aria-label={`${user?.first_name} ${user?.last_name}`}
            onClick={handleToggle}
            data-qat="profile-dropdown"
          >
            {user && (
              <Avatar
                fontStyle={{ fontFamily: uiStore.branding.font_family }}
                size={32}
                name={`${user?.first_name} ${user?.last_name}`}
                url={user?.photo_url}
              />
            )}
          </Button>
        </ButtonGroup>
      </div>
      <Popper
        aria-label="Profile Options"
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
      >
        {({ TransitionProps, placement }) => (
          <Grow
            timeout={{ appear: 200, enter: 200, exit: 200 }}
            {...TransitionProps}
            style={{
              zIndex: 9999,
              transformOrigin: placement === 'bottom' ? 'center top' : 'center left',
            }}
          >
            <Paper aria-label="Profile Options" style={DROPDOWN} variant="outlined">
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList style={{ padding: 0 }} id="split-button-menu">
                  <div ref={logOutElement}>
                    <MenuItem
                      style={PROFILE_CONTAINER}
                      disabled
                      onClick={() => handleMenuItemClick(0)}
                      className={classes.navItem}
                    >
                      <Avatar
                        fontStyle={{ fontFamily: uiStore.branding.font_family }}
                        size={32}
                        name={`${user?.first_name} ${user?.last_name}`}
                        url={user?.photo_url}
                      />
                      <div style={{ flexDirection: 'column', marginLeft: 8, display: 'flex' }}>
                        {user?.first_name && user?.last_name && (
                          <Text
                            style={{ fontFamily: uiStore.branding.font_family }}
                            text={`${user?.first_name} ${user?.last_name}`}
                            variant="body1Emphasized"
                          />
                        )}
                        <Text
                          text={user?.email}
                          variant="body1Medium"
                          style={{ color: color.shade50, fontFamily: uiStore.branding.font_family }}
                        />
                      </div>
                    </MenuItem>
                    <Divider tabIndex={-1} />
                    <div
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleMenuItemClick(3)
                        }
                      }}
                      role="link"
                      tabIndex={0}
                    >
                      <MenuItem
                        onClick={() => handleMenuItemClick(3)}
                        style={MENU_ITEM_3}
                        className={classes.navItem}
                        data-qat="logout"
                      >
                        <Text
                          style={{
                            fontFamily: uiStore.branding.font_family,
                            fontWeight: 500,
                          }}
                          tx="Onboarding.Login.logOut"
                          variant="body1Medium"
                        />
                      </MenuItem>
                    </div>
                  </div>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  )
})

const AVATAR_BUTTON: React.CSSProperties = {
  background: 'none',
  borderRadius: '1000px',
  height: 32,
  width: 32,
  padding: 0,
  minWidth: 32,
}
const BUTTON_CONTAINER: React.CSSProperties = {
  background: 'none',
  padding: 0,
  borderRadius: '1000px',
  height: 32,
  width: 32,
  boxShadow: 'none',
  minWidth: 32,
}
const DROPDOWN: React.CSSProperties = {
  zIndex: 9999,
  position: 'relative',
  top: 110,
  left: 45,
  width: 233,
  borderRadius: 10,
  padding: 0,
  boxShadow: '0px 4px 8px 8px rgba(0,0,0,0.03)',
  display: 'inline-table',
}
const CONTAINER: React.CSSProperties = {
  alignItems: 'center',
  display: 'flex',
}

const PROFILE_CONTAINER: React.CSSProperties = {
  paddingBottom: 22,
  paddingTop: 22,
  paddingRight: 24,
  height: 78,
  width: '100%',
  flexDirection: 'row',
  borderTopLeftRadius: 10,
  borderTopRightRadius: 10,
  opacity: 1.0,
}
const MENU_ITEM: React.CSSProperties = {
  paddingBottom: 22,
  paddingTop: 22,
  paddingLeft: 24,
  paddingRight: 24,
  height: 46,
  width: '100%',
  flexDirection: 'row',
}
const MENU_ITEM_3: React.CSSProperties = {
  ...MENU_ITEM,
  height: 60,
  borderBottomLeftRadius: 10,
  borderBottomRightRadius: 10,
}
